import {
  DashboardColumnType,
  IDashboardImageEmbedEditMutation,
  IDashboardImageEmbedEditMutationVariables,
  IDashboardInstanceFieldsFragment,
  IImageEmbedFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useCallback, useMemo, useRef } from 'react';
import useDashboardUpdateGrid from '../grid/useDashboardUpdateGrid';
import {
  IImageEmbedUploadHandle,
  IImageEmbedUploadProps,
} from '../../../../components/ImageUploady/ImageEmbedUpload';
import { getRowsWithUpdatedContent } from '../grid/gridUpdaters';
import { useMutation } from '@apollo/client';
import { DASHBOARD_IMAGE_EMBED_EDIT } from '../../../../utils/apollo/dashboards';
import { assertNotNil } from '../../../../utils/types';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import useDashboardReload from '../useDashboardReload';

/**
 * Image upload is quite cumbersome and works with ImageEmbedUpload component.
 *
 * It happens in several phases:
 * 1. Open file upload (startImageEmbedEditUpload) via Uploady library and ImageEmbedUpload
 * 2. Uploady library receives the file selected by the user
 * 3. We create an upload destination (createImageEmbedUploadConfig)
 * 4 We handle success or failure of the upload (handleImageEmbedEditSuccess or handleImageEmbedEditError)
 *
 * Additionally, we pass the existing ImageEmbed as context property of the ImageEmbedUpload
 * so we can use it later in the handleImageEmbedEditSuccess to update the grid with the updated image
 *
 * This hook produces the necessary props to configure ImageEmbedUpload component at usage side
 *
 * @param dashboard
 * @param setLoading
 */

const useDashboardEditImageEmbed = (
  dashboard: IDashboardInstanceFieldsFragment,
  setLoading: (loading: boolean) => void,
) => {
  const { updateDashboardGrid } = useDashboardUpdateGrid(dashboard);
  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const [editImageEmbedMutation] = useMutation<
    IDashboardImageEmbedEditMutation,
    IDashboardImageEmbedEditMutationVariables
  >(DASHBOARD_IMAGE_EMBED_EDIT);

  const imageEmbedEditUploadHandle =
    useRef<IImageEmbedUploadHandle<IImageEmbedFieldsFragment>>(null);

  const startImageEmbedEditUpload = useCallback(
    (imageEmbed: IImageEmbedFieldsFragment) => {
      assertNotNil(imageEmbedEditUploadHandle.current);
      imageEmbedEditUploadHandle.current.startUpload(imageEmbed);
    },
    [],
  );

  const createImageEmbedUploadConfig = useCallback(
    async (imageEmbed: IImageEmbedFieldsFragment) => {
      setLoading(true);
      try {
        const resp = await editImageEmbedMutation({
          variables: {
            input: {
              baseId: dashboard.baseId,
              instanceId: dashboard.id,
              imageEmbedId: imageEmbed.id,
            },
          },
        });
        const result = resp.data?.dashboardImageEmbedEdit;
        if (printGraphqlErrors(result?.errors)) {
          throw new Error('Failed to create image embed upload config');
        }
        assertNotNil(result?.uploadConfig);

        return result.uploadConfig;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, editImageEmbedMutation, dashboard.baseId, dashboard.id],
  );

  const handleImageEmbedEditSuccess = useCallback(
    async (imageEmbed: IImageEmbedFieldsFragment) => {
      updateDashboardGrid(
        getRowsWithUpdatedContent({
          type: DashboardColumnType.ImageEmbed,
          imageEmbed,
        }),
      );
    },
    [updateDashboardGrid],
  );

  const handleImageEmbedEditError = useCallback(async () => {
    setLoading(true);
    try {
      await reloadDashboard();
    } finally {
      setLoading(false);
    }
  }, [reloadDashboard, setLoading]);

  const imageEmbedUploadProps = useMemo(
    () =>
      ({
        ref: imageEmbedEditUploadHandle,
        onUploadSuccess: handleImageEmbedEditSuccess,
        onUploadFailed: handleImageEmbedEditError,
        createImageEmbedUploadConfig,
      }) satisfies IImageEmbedUploadProps<IImageEmbedFieldsFragment>,
    [
      createImageEmbedUploadConfig,
      handleImageEmbedEditError,
      handleImageEmbedEditSuccess,
    ],
  );

  return {
    startImageEmbedEditUpload,
    imageEmbedUploadProps,
  };
};

export default useDashboardEditImageEmbed;
